import { useMemo, lazy, Suspense } from 'react'

import { HeaderProps, SharedHeader } from '@verbit-ai/shared-header'
import { Box } from '@chakra-ui/react'

import { useResponsive } from 'src/hooks/useResponsive'
import { useUserContext } from 'src/contexts/UserProvider'
import { useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { useVerbitConnectAvailability } from 'src/hooks/useVerbitConnectAvailability'
import { useFerretSearch } from 'src/contexts/FerretSearchWrapperProvider'
import { getCollectionId, getEnv, isMockActive } from 'src/utils/env'
import { analyticsService } from 'src/services/AnalyticsService'
import { rollbarService } from 'src/services/RollbarService'
import { useSetThemeField } from 'src/contexts/ThemeContext'
import { HEADER_HEIGHT_EXTENDED } from 'src/themes/types'
import { getSessionTitle, isJobLive } from 'src/models/TranscriptMetadata'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { CustomerResponse } from 'src/network/types'

import * as S from './styles'

// to prevent SharedHeader from making an extra request to GET /customer endpoint,
// we use this as a fallback while fetching actual data
const DEFAULT_CUSTOMER: CustomerResponse['customer'] = {
    id: '',
    name: '',
    type: 'education',
    access_to_legal_deposition: false,
} as const

const MediaSourceTimer = lazy(() =>
    import('@verbit-ai/verbit-connect-web/media-source-timer').then((module) => ({ default: module.MediaSourceSessionTimer })),
)
const FerretSearch = lazy(() => import('@verbit-ai/ferret-web').then((module) => ({ default: module.FerretSearch })))

const onMixpanelEvent = (event: string, data?: Record<string, unknown>) => {
    analyticsService.track(event, data || {})
}
const onRollbarError = rollbarService.error.bind(rollbarService)

export const Topbar = () => {
    const transcriptMetadata = useTranscriptMetadata()
    const { isMobilePhone } = useResponsive()
    const setThemeField = useSetThemeField()

    const { ferretSearchRef, shouldShowFerretSearch } = useFerretSearch()

    const { isMediaSourceAvailable } = useVerbitConnectAvailability()
    const shouldDisplayMediaSourceTimer = isMobilePhone && isMediaSourceAvailable
    const shouldDisplaySessionTitle = !shouldDisplayMediaSourceTimer
    const sessionTitle = getSessionTitle(transcriptMetadata)

    const environment = getEnv()
    const collectionId = getCollectionId()
    const jobId = transcriptMetadata?.transcriptionJobId ? String(transcriptMetadata.transcriptionJobId) : undefined
    const shouldFerretUseMocks = isMockActive()
    const enableAbsoluteTimestamps = useFeatureFlag('absolute_timestamps')
    const rawRecordingStartedAt = transcriptMetadata?.sessionState?.recording?.startedAt ?? transcriptMetadata?.sessionStartedAt
    // Only pass to FerretSearch when the feature flag is on; ferret-web falls
    // back to relative timestamps automatically when this is undefined.
    const recordingStartedAt = enableAbsoluteTimestamps ? rawRecordingStartedAt : undefined
    const sessionStartedAt = useMemo(() => {
        if (!rawRecordingStartedAt) {
            return null
        }

        const startedAt = new Date(rawRecordingStartedAt)

        return Number.isNaN(startedAt.getTime()) ? null : startedAt
    }, [rawRecordingStartedAt])

    const ferretSearch = () => (
        <>
            {shouldShowFerretSearch && (
                <Box margin={'auto'}>
                    <Suspense>
                        <FerretSearch
                            caseId={collectionId || ''}
                            jobId={jobId}
                            environment={environment}
                            isJobLive={transcriptMetadata ? isJobLive(transcriptMetadata) : false}
                            recordingStartedAt={recordingStartedAt}
                            shouldUseMockData={shouldFerretUseMocks}
                            onAnalyticsEvent={onMixpanelEvent}
                            onError={onRollbarError}
                            ref={ferretSearchRef}
                        />
                    </Suspense>
                </Box>
            )}
        </>
    )

    const onImpersonationRendered = () => {
        setThemeField('headerHeight', HEADER_HEIGHT_EXTENDED)
    }

    const { customer: contextCustomer } = useUserContext()
    const mockHeaderConfig = useMemo(() => (isMockActive() ? document.mocks.getSharedHeaderConfig() : {}), [])
    const customer: HeaderProps['customer'] = contextCustomer ?? mockHeaderConfig.customer ?? DEFAULT_CUSTOMER

    return (
        <S.Bar>
            <SharedHeader
                title={shouldDisplaySessionTitle ? sessionTitle : ''}
                analytics={(event, data) => analyticsService.track(event, data || {})}
                onImpersonationRendered={onImpersonationRendered}
                userConfig={mockHeaderConfig.userConfig}
                customer={customer}>
                {shouldDisplayMediaSourceTimer && <MediaSourceTimer sessionStartedAt={sessionStartedAt} />}
                {ferretSearch()}
            </SharedHeader>
        </S.Bar>
    )
}
